import sortedUniq from 'lodash/sortedUniq';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { CommandCenterHousehold, PersonOrgType } from '@ndvr/customer-api';

import { Sheet, SheetBody, SheetContent, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { useInternalUserDisplayName } from '@/modules/internal-user-queries';
import { AdvisorLoginButton } from 'modules';
import {
    formatCurrency,
    formatDate,
    getClientName,
    getHouseholdName,
    useHouseholdRoutePaths,
    useHouseholdRouteType,
} from 'utils';
import { useClientProfilesForHousehold } from 'views/client/client-queries';
import { Button, IconLogoutSquare, PersonLogoCard, PersonOrgLogoCard, PortfolioLogoCard, Text, Title } from 'widgets';
import { DiamondAvatar } from 'widgets/Avatar';
import { NotFoundTile } from 'widgets/drawer/NotFoundTile';
import { HorizontalLayout, VerticalLayout } from 'widgets/Layout';

interface HouseholdDetailsDrawerProps {
    household: CommandCenterHousehold;
    onClose: () => void;
}

export const HouseholdDetailsDrawer = (props: HouseholdDetailsDrawerProps) => {
    const { household: household, onClose } = props;
    const { PortfolioOverviewTabRoutePath, PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const { isProspectRoute } = useHouseholdRouteType();
    const { colors } = useTheme();
    const { data: clientProfiles } = useClientProfilesForHousehold(household.globalAccountGUID);
    const getInternalUserName = useInternalUserDisplayName();
    const hasClientProfiles = clientProfiles && clientProfiles?.length > 0;

    const portfolioProfiles = useMemo(
        () => (clientProfiles || []).map((clientProfile) => clientProfile.portfolioProfiles).flat(),
        [clientProfiles],
    );

    const hasPortfolioProfiles = portfolioProfiles?.length > 0;

    const firstFundingDate = useMemo(() => {
        const firstFundingDates = portfolioProfiles.map((p) => p.portfolio?.firstFundingDate).filter((date) => date);
        return sortedUniq(firstFundingDates).at(0);
    }, [portfolioProfiles]);

    return (
        <Sheet defaultOpen onExitComplete={onClose}>
            <SheetContent aria-describedby={undefined} layout="scroll-body" size="sm">
                <SheetTitle className="sr-only">{getHouseholdName(household)}</SheetTitle>
                <SheetBody>
                    <Section $columnCenter $rowCenter $gap={24}>
                        <DiamondAvatar size={64} color={colors.cocoa200}>
                            <Title page>{getHouseholdName(household).charAt(0)}</Title>
                        </DiamondAvatar>
                        <Title page>{getHouseholdName(household)}</Title>
                    </Section>
                    <Separator />
                    <InfoSection>
                        <Info title={formatCurrency(household?.aum, 'USD') ?? '-'} subTitle="AUM" />
                        <Info title={clientProfiles?.length ?? '-'} subTitle="Owners" />
                        <Info title={portfolioProfiles?.length ?? '-'} subTitle="Accounts" />
                    </InfoSection>
                    <Separator />
                    <Section $gap={16}>
                        <Title section>Details</Title>
                        <Detail title="GUID" subTitle={household.globalAccountGUID} />
                        <Detail title="Primary Advisor" subTitle={getInternalUserName(household.primaryAdvisorGUID)} />
                        <Detail
                            title={isProspectRoute ? 'Prospect Created' : 'Household Created'}
                            subTitle={formatDate(household.createdAt)}
                        />
                        {firstFundingDate ? (
                            <Detail title="First Funded" subTitle={formatDate(firstFundingDate)} />
                        ) : null}
                    </Section>
                    <Separator />
                    <Section $gap={16}>
                        <Title section>Account Owners</Title>
                        {!hasClientProfiles ? <NotFoundTile title="No account owners defined" /> : null}
                        {(clientProfiles ?? []).map((clientProfile) =>
                            clientProfile.clientPersonOrgProfile.personOrg.personOrgType === PersonOrgType.PERSON ? (
                                <PersonLogoCard
                                    key={clientProfile.client.personOrgGUID}
                                    personInfo={clientProfile.clientPersonOrgProfile.personOrg}
                                />
                            ) : (
                                <PersonOrgLogoCard
                                    key={clientProfile.clientPersonOrgProfile.personOrg.personOrgGUID}
                                    name={getClientName(clientProfile)}
                                    personOrgType={clientProfile.clientPersonOrgProfile.personOrg.personOrgType}
                                    to={PortfolioOverviewTabRoutePath(clientProfile.client.globalAccountGUID)}
                                />
                            ),
                        )}
                    </Section>
                    <Separator />
                    <Section $gap={16}>
                        <Title section>Accounts</Title>
                        {!hasPortfolioProfiles ? <NotFoundTile title="No accounts defined" /> : null}
                        {portfolioProfiles.map((portfolioProfile) => (
                            <PortfolioLogoCard
                                key={portfolioProfile.portfolio.portfolioGUID}
                                name={portfolioProfile.portfolio.name.plainText}
                                portfolioTaxType={
                                    portfolioProfile.portfolio.investmentSetup.portfolioTaxProfile.portfolioTaxType
                                }
                                to={PortfolioPageRoutePath(
                                    portfolioProfile.portfolio.globalAccountGUID,
                                    portfolioProfile.portfolio.accountGroupGUID,
                                )}
                            />
                        ))}
                    </Section>
                </SheetBody>
                <SheetFooter variant="full-width">
                    <ButtonSection globalAccountGUID={household.globalAccountGUID} isProspectRoute={isProspectRoute} />
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

const Section = styled(VerticalLayout)`
    position: relative;
    padding: 1.5rem;
`;

const Separator = styled.div`
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.border.light};
`;

const InfoSection = styled(HorizontalLayout)`
    padding: 1.5rem;
    justify-content: space-around;
`;

const Info = ({ title, subTitle }: { title?: string | number; subTitle: string }) => (
    <VerticalLayout $columnCenter $gap={8}>
        <Title modal>{title}</Title>
        <Text body>{subTitle}</Text>
    </VerticalLayout>
);

const Detail = ({ title, subTitle }: { title: string; subTitle: string }) => {
    return (
        <DetailLayout>
            <Text body>{title}</Text>
            <Text body>{subTitle}</Text>
        </DetailLayout>
    );
};

const DetailLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    & > *:last-child {
        text-align: right;
    }
`;

const ButtonSection = ({
    globalAccountGUID,
    isProspectRoute,
}: Pick<CommandCenterHousehold, 'globalAccountGUID'> & { isProspectRoute: boolean }) => {
    const navigate = useNavigate();
    const { HouseholdIndividualPageRoutePath } = useHouseholdRoutePaths();
    return (
        <VerticalLayout>
            <Separator />
            <ActionsHorizontalLayout $gap={8}>
                <ViewHouseholdsButton
                    buttonType="secondary"
                    size="large"
                    onClick={() => navigate(HouseholdIndividualPageRoutePath(globalAccountGUID))}
                >
                    {isProspectRoute ? 'View Prospect' : 'View Household'}
                </ViewHouseholdsButton>
                <AdvisorLoginButton size="icon" redirect="/overview" globalAccountGUID={globalAccountGUID} />
            </ActionsHorizontalLayout>
        </VerticalLayout>
    );
};

const ActionsHorizontalLayout = styled(HorizontalLayout)`
    border-top: 1px solid ${({ theme }) => theme.border.light};
    padding: 1.5rem;
`;

const ViewHouseholdsButton = styled(Button)`
    width: 100%;
`;

const LoginButtonWrapper = styled(VerticalLayout)`
    padding: 0.75rem;
    color: ${({ theme }) => theme.colors.cocoa500};
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.border.dark};
`;
